import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';

import FilterBuilder from '../../../../components/filter/filterbuilder';
import DonutChart from '../../../../components/charts/donutchart';

import DataContext from '../../../../data/data-context.js';
import DashboardService from '../../../../data/dashboard-service';
import AlertsService from '../../../../data/alerts-service';
import Auth, { ParamsSerializer } from '../../../../data/auth-service';
import { GetISODateFromQuick } from '../../../../libraries/helpers';
import DateRange from './../components/date-range';
import { VerifyDates } from '../../widget';
import { get as _get } from 'lodash';

const useStyles = makeStyles(theme => ({
    // editor classes
    editorRoot: {
        padding: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    filterText: {
        marginLeft: 10,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 10 }
    : { minW: 3, minH: 10 };

// don't display until size-me can figure out what our size is
const AlertsSeverityWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
AlertsSeverityWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const Widget = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [, setErrors] = useState(0);
    const [start, setStart] = useState(props.data.start);
    const [end, setEnd] = useState();

    const alertPerms = Auth.permissions.get('Alerts');

    const fetch = controller => {
        props.setBusy(true);
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        if (alertPerms.read) {
            return DashboardService.getAlertGroups({
                start,
                end,
                filter: props.data.settings.json_filter,
                group: ['severity'],
                timeline: false,
                limit: 5,
            }, controller).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            });
        }
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);

        setData(res || []);
    };

    const subscribe = () => {
        setErrors(0);
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const getSize = () => {
        return props.size.width ? Math.min(props.size.width, props.size.height) - 5 : 150;
    };

    const handleDonutClick = filter => {
        if (!filter) return;

        const f = {
            operator: 'And',
            nodes: [{ variable: 'severity', value: `${filter.value[0]}` }],
        };
        if (_get(props.data, 'settings.json_filter.nodes', []).length > 0) {
            f.nodes.push(props.data.settings.json_filter);
        }
        const p = {
            start,
            end,
            quick: props.data.quick,
            filter: JSON.stringify(f),
        };
        navigate(`/views/alerts?${ParamsSerializer(p)}`);
    };

    useEffect(() => {
        setErrors(0);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, []);

    useEffect(() => {
        subscribe();
        setStart(props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start);
        setEnd(props.data.quick ? undefined : props.data.end);
    }, [props.data.start, props.data.id, props.data.end, props.data.quick]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height }}>
            <DonutChart
                data={data}
                size={getSize()}
                onclick={handleDonutClick}/>
        </div>
    );
};
Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const daForm = useValidate({});
    const { assets, sensorNames, backtestingJobsLite } = useContext(DataContext);

    const [title, setTitle] = useState(props.data.title);
    const [filter, setFilter] = useState(props.data.settings.json_filter);
    const [fields, setFields] = useState([]);
    const [showFilter, setShowFilter] = useState(false);
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleFilterChange = ({ filter }) => {
        setFilter(filter);
        setShowFilter(false);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                json_filter: filter,
            },
        });
    };

    const fetchFields = async () => {
        setFields(AlertsService.fields.filter(c => c.indexed && c.type !== 'timestamp'));
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;

        const formState = daForm.get() || {};
        if (Object.keys(formState).length === 0 && title && title.trim().length > 0) {
            return true;
        }
        return checkValidity(formState);
    };

    // initial hook
    useEffect(() => {
        fetchFields();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    {props.data.date_override &&
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    }
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <FilterBuilder
                                onOK={handleFilterChange}
                                onShow={setShowFilter}
                                show={showFilter}
                                fields={fields}
                                filter={filter}
                                placement="right-start"
                                button={
                                    <Button
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AccountTreeIcon/>}>Filter
                                    </Button>
                                }/>
                            <Typography
                                variant="body2"
                                component="span"
                                className={classes.filterText}>
                                {ReactHtmlParser(AlertsService.toEnglish(
                                    filter,
                                    true,
                                    sensorNames,
                                    assets,
                                    backtestingJobsLite))}
                            </Typography>
                        </Grid>
                    </Grid>
                </div>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="secondary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    AlertsSeverityWidget as default,
    DEFAULTS,
};
